import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Stack,
  Divider,
  alpha,
  useTheme,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Security as SecurityIcon,
  Link as LinkIcon,
  PersonAdd as PersonAddIcon,
  LinkOff as LinkOffIcon,
  Article as ArticleIcon,
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  VideoFile as VideoFileIcon,
  AudioFile as AudioFileIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import ProgressTracker from '../components/common/ProgressTracker';
import MultiLoginDialog from '../components/common/MultiLoginDialog';
import { useUser } from '../contexts/UserContext';
import API_CONFIG from '../config/api';
import { useSEO } from '../hooks/useSEO';

// 辅助函数：获取状态文本
const getStatusText = (phase) => {
  const statusMap = {
    'preparation': '准备阶段',
    'application': '申请阶段', 
    'visa': '签证阶段',
    'enrollment': '入学阶段',
    'completed': '已完成'
  };
  return statusMap[phase] || '未知状态';
};

// 辅助函数：获取状态颜色
const getStatusColor = (phase) => {
  const colorMap = {
    'preparation': 'info',
    'application': 'warning',
    'visa': 'primary',
    'enrollment': 'success',
    'completed': 'success'
  };
  return colorMap[phase] || 'default';
};

// 辅助函数：获取文件类型图标
const getFileIcon = (fileName) => {
  if (!fileName) return <AttachFileIcon />;
  
  const extension = fileName.split('.').pop().toLowerCase();
  
  switch (extension) {
    case 'pdf':
      return <PictureAsPdfIcon sx={{ color: 'error.main' }} />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'webp':
      return <ImageIcon sx={{ color: 'primary.main' }} />;
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
    case 'flv':
      return <VideoFileIcon sx={{ color: 'secondary.main' }} />;
    case 'mp3':
    case 'wav':
    case 'flac':
    case 'aac':
      return <AudioFileIcon sx={{ color: 'info.main' }} />;
    case 'doc':
    case 'docx':
    case 'txt':
    case 'rtf':
      return <DescriptionIcon sx={{ color: 'success.main' }} />;
    default:
      return <AttachFileIcon sx={{ color: 'text.secondary' }} />;
  }
};

// 辅助函数：格式化文件大小
const formatFileSize = (bytes) => {
  if (!bytes) return '未知大小';
  
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

// 辅助函数：转换进度详情
const convertProgressDetails = (progressDetails) => {
  if (!progressDetails) return {};
  
  const converted = {};
  
  // 转换每个阶段的进度数据
  Object.keys(progressDetails).forEach(phaseKey => {
    const phaseData = progressDetails[phaseKey];
    if (!phaseData) return;
    
    converted[phaseKey] = {
      status: getPhaseStatus(phaseData),
      stepStatuses: {},
      stepDetails: {}
    };
    
    // 转换步骤状态
    Object.keys(phaseData).forEach(stepKey => {
      const stepData = phaseData[stepKey];
      if (stepData) {
        converted[phaseKey].stepStatuses[stepKey] = stepData.completed ? 'completed' : 'pending';
        converted[phaseKey].stepDetails[stepKey] = {
          status: stepData.completed ? 'completed' : 'pending',
          required: stepData.required,
          name: stepData.name
        };
      }
    });
  });
  
  return converted;
};

// 辅助函数：获取阶段状态
const getPhaseStatus = (phaseData) => {
  if (!phaseData) return 'pending';
  
  const steps = Object.values(phaseData);
  const completedSteps = steps.filter(step => step.completed);
  
  if (completedSteps.length === 0) return 'pending';
  if (completedSteps.length === steps.length) return 'completed';
  return 'in_progress';
};

const ApplicationQuery = () => {
  const theme = useTheme();
  const { isLoggedIn, isLoading: userContextLoading } = useUser(); // 使用UserContext获取登录状态和加载状态
  const [formData, setFormData] = useState({
    chineseName: '',
    userNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [applicationData, setApplicationData] = useState(null);
  const [personalInfoExpanded, setPersonalInfoExpanded] = useState(false);
  // 移除验证码相关状态，现在使用JWT认证
  const [activeApplicationIndex, setActiveApplicationIndex] = useState(0);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [isBinding, setIsBinding] = useState(false);
  const [isUnbinding, setIsUnbinding] = useState(false);
  const [isUserStudentLoaded, setIsUserStudentLoaded] = useState(false);
  const [showBindButton, setShowBindButton] = useState(false);
  const [isBound, setIsBound] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    action: null
  });
  const [guideDialog, setGuideDialog] = useState({
    open: false,
    title: '',
    content: '',
    attachments: []
  });
  const [passportVerificationDialog, setPassportVerificationDialog] = useState({
    open: false,
    passportNumber: '',
    error: ''
  });

  // 设置页面 SEO
  useSEO('applicationQuery');

  // 页面加载时滚动到顶部
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 自动查询用户关联的学生信息
  useEffect(() => {
    const loadUserStudent = async () => {
      // 等待 UserContext 加载完成
      if (userContextLoading) {
        setIsPageLoading(true);
        return;
      }
      
      // 页面加载时显示loading状态
      setIsPageLoading(true);
      
      if (!isLoggedIn) {
        // 如果未登录，直接显示登录提示
        setIsPageLoading(false);
        return;
      }
      
      if (isUserStudentLoaded) {
        setIsPageLoading(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        const response = await fetch(API_CONFIG.BASE_URL + API_CONFIG.APPLICATION_QUERY.USER_STUDENT, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        
        const result = await response.json();
        
        console.log('用户关联学生信息查询结果:', result);
        
        if (result.success && result.data) {
          // 用户已有关联的学生信息，直接显示
          const applicationData = result.data;
          
          // 转换后端数据格式为前端期望的格式
          console.log('原始数据:', applicationData);
          
          // 数据已经在正确的格式，直接使用
          const convertedData = {
            studentName: applicationData.studentName || '未知',
            passportNumber: applicationData.passportNumber,
            userNumber: applicationData.userNumber,
            applications: applicationData.applications ? applicationData.applications.map(app => ({
              applicationId: app.applicationId,
              universityName: app.universityName,
              programName: app.programName,
              universityLogo: app.universityLogo,
              universityFullName: app.universityFullName,
              currentStatus: app.currentPhase,
              currentProgress: app.currentProgress,
              statusInfo: {
                text: getStatusText(app.currentPhase),
                color: getStatusColor(app.currentPhase)
              },
              progressDetails: app.progressDetails,
              guides: app.guides || [] // 添加guides字段
            })) : [],
            personalInfo: applicationData.personalInfo
          };
          console.log('转换后的数据:', convertedData);
          setApplicationData(convertedData);
          setShowBindButton(false); // 已绑定，不显示绑定按钮
          setIsBound(true); // 设置为已绑定状态
          console.log('已设置 applicationData');
        } else {
          // 用户没有关联的学生信息，显示查询表单
          setShowBindButton(true);
        }
      } catch (err) {
        console.error('查询用户关联学生信息错误:', err);
        setShowBindButton(true);
      } finally {
        setIsUserStudentLoaded(true);
        setIsPageLoading(false);
      }
    };

    loadUserStudent();
  }, [isLoggedIn, isUserStudentLoaded, userContextLoading]);

  // 移除自定义登录状态检查，现在使用UserContext

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    setError('');
  };

  // 移除验证码相关函数，现在使用JWT认证

  // 显示确认对话框
  const showConfirmDialog = (title, content, action) => {
    setConfirmDialog({
      open: true,
      title,
      content,
      action
    });
  };

  // 关闭确认对话框
  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: '',
      content: '',
      action: null
    });
  };

  // 确认执行操作
  const handleConfirmAction = () => {
    if (confirmDialog.action) {
      confirmDialog.action();
    }
    closeConfirmDialog();
  };

  // 显示指南文章
  const showGuideDialog = (title, content, attachments = []) => {
    setGuideDialog({
      open: true,
      title,
      content,
      attachments
    });
  };

  // 关闭指南文章对话框
  const closeGuideDialog = () => {
    setGuideDialog({
      open: false,
      title: '',
      content: '',
      attachments: []
    });
  };

  // 显示护照号验证对话框
  const showPassportVerificationDialog = () => {
    setPassportVerificationDialog({
      open: true,
      passportNumber: '',
      error: ''
    });
  };

  // 关闭护照号验证对话框
  const closePassportVerificationDialog = () => {
    setPassportVerificationDialog({
      open: false,
      passportNumber: '',
      error: ''
    });
  };

  // 处理护照号输入变化
  const handlePassportNumberChange = (event) => {
    setPassportVerificationDialog(prev => ({
      ...prev,
      passportNumber: event.target.value,
      error: ''
    }));
  };

  // 解绑学生信息
  const handleUnbindStudent = async () => {
    setIsUnbinding(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(API_CONFIG.BASE_URL + API_CONFIG.APPLICATION_QUERY.UNBIND_STUDENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      const result = await response.json();
      
      if (result.success) {
        // 解绑成功，清空数据并显示查询表单
        setApplicationData(null);
        setShowBindButton(false);
        setIsBound(false);
        setError('');
        setIsUserStudentLoaded(false);
        setIsPageLoading(true);
        console.log('解绑成功');
      } else {
        setError(result.message || '解绑失败，请重试');
      }
    } catch (err) {
      console.error('解绑学生错误:', err);
      setError('解绑失败，请重试');
    } finally {
      setIsUnbinding(false);
    }
  };

  // 绑定学生到当前用户（带护照号验证）
  const handleBindStudent = async () => {
    if (!applicationData || !applicationData.userNumber) {
      setError('无法获取学生信息进行绑定');
      return;
    }

    // 验证护照号输入
    if (!passportVerificationDialog.passportNumber.trim()) {
      setPassportVerificationDialog(prev => ({
        ...prev,
        error: '请输入护照号'
      }));
      return;
    }

    setIsBinding(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(API_CONFIG.BASE_URL + API_CONFIG.APPLICATION_QUERY.BIND_STUDENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          chinese_name: applicationData.personalInfo?.nameCn,
          user_number: applicationData.userNumber,
          passport_number: passportVerificationDialog.passportNumber.trim()
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setShowBindButton(false);
        setError(''); // 清除错误信息
        closePassportVerificationDialog(); // 关闭护照号验证对话框
        
        // 绑定成功后，重新查询用户关联的学生信息
        try {
          const userStudentResponse = await fetch(API_CONFIG.BASE_URL + API_CONFIG.APPLICATION_QUERY.USER_STUDENT, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
          
          const userStudentResult = await userStudentResponse.json();
          
          if (userStudentResult.success && userStudentResult.data) {
            // 转换后端数据格式为前端期望的格式
            const applicationData = userStudentResult.data;
            
            // 数据已经在正确的格式，直接使用
            const convertedData = {
              studentName: applicationData.studentName || '未知',
              passportNumber: applicationData.passportNumber,
              userNumber: applicationData.userNumber,
              applications: applicationData.applications ? applicationData.applications.map(app => ({
                applicationId: app.applicationId,
                universityName: app.universityName,
                programName: app.programName,
                universityLogo: app.universityLogo,
                universityFullName: app.universityFullName,
                currentStatus: app.currentPhase,
                currentProgress: app.currentProgress,
                statusInfo: {
                  text: getStatusText(app.currentPhase),
                  color: getStatusColor(app.currentPhase)
                },
                progressDetails: app.progressDetails,
                guides: app.guides || [] // 添加guides字段
              })) : [],
              personalInfo: applicationData.personalInfo
            };
            setApplicationData(convertedData);
            setShowBindButton(false); // 已绑定，不显示绑定按钮
            setIsBound(true); // 设置为已绑定状态
          }
        } catch (err) {
          console.error('重新查询用户关联学生信息错误:', err);
        }
        
        console.log('绑定成功');
      } else {
        // 如果是护照号验证失败，在对话框中显示错误
        if (result.message && result.message.includes('护照号')) {
          setPassportVerificationDialog(prev => ({
            ...prev,
            error: result.message
          }));
        } else {
          setError(result.message || '绑定失败，请重试');
        }
      }
    } catch (err) {
      console.error('绑定学生错误:', err);
      setError('绑定失败，请重试');
    } finally {
      setIsBinding(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // 简单验证
    if (!formData.chineseName.trim()) {
      setError('请输入中文姓名');
      return;
    }
    
    if (!formData.userNumber.trim()) {
      setError('请输入编号');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      // 调用真实API
      const queryData = {
        chineseName: formData.chineseName,
        userNumber: formData.userNumber
      };
      
      // 检查登录状态
      if (!isLoggedIn) {
        setError('请先登录后再查询申请进度');
        return;
      }

      // 获取JWT token
      const token = localStorage.getItem('auth_token');

      const response = await fetch(API_CONFIG.BASE_URL + API_CONFIG.APPLICATION_QUERY.SEARCH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          chinese_name: queryData.chineseName,
          user_number: queryData.userNumber
        }),
      });
      
      const result = await response.json();
      
      // 检查API响应是否成功
      if (!result.success) {
        setError(result.message || '查询失败，请重试');
        return;
      }
      
      // 使用API响应数据，如果API返回空数据则使用模拟数据
      let applicationData = result.data;
      
      // 转换后端数据格式为前端期望的格式
      if (applicationData && applicationData.student) {
        applicationData = {
          studentName: applicationData.student.name_cn || applicationData.student.name || '未知',
          passportNumber: applicationData.student.travel_doc_no,
          userNumber: applicationData.student.serial_no,
          applications: applicationData.applications ? applicationData.applications.map(app => ({
            applicationId: app.applicationId,
            universityName: app.universityName,
            programName: app.programName,
            universityLogo: app.universityLogo,
            universityFullName: app.universityFullName,
            currentStatus: app.currentPhase,
            currentProgress: app.currentProgress,
            statusInfo: {
              text: getStatusText(app.currentPhase),
              color: getStatusColor(app.currentPhase)
            },
            progressDetails: app.progressDetails,
            guides: app.guides || [] // 添加guides字段
          })) : []
        };
      }
      
      // 如果API没有返回数据或返回空数据，使用模拟数据
      if (!applicationData || !applicationData.studentName) {
        console.log('API返回空数据，使用模拟数据');
        applicationData = {
        studentName: '张三',
        passportNumber: formData.chineseName,
        userNumber: formData.userNumber,
        applications: [
          {
            applicationId: 'APP2024001',
            universityName: '剑桥大学',
            programName: '计算机科学硕士',
            currentStatus: 'application',
            statusInfo: {
              text: '申请阶段',
              color: 'warning'
            },
            progress: {
              preparation: {
                status: 'completed',
                stepStatuses: {
                  personal_info: 'completed',
                  application_materials: 'completed',
                  application_fee: 'completed',
                  document_translation: 'completed',
                  document_notarization: 'completed'
                },
                stepDetails: {
                  personal_info: { status: 'completed', completedAt: '2024-01-15 10:30:00' },
                  application_materials: { status: 'completed', completedAt: '2024-01-20 14:20:00' },
                  application_fee: { status: 'completed', completedAt: '2024-01-22 09:15:00' },
                  document_translation: { status: 'completed', completedAt: '2024-01-25 11:30:00' },
                  document_notarization: { status: 'completed', completedAt: '2024-01-28 15:45:00' }
                }
              },
              application: {
                status: 'in_progress',
                stepStatuses: {
                  material_review: 'completed',
                  application_submit: 'completed',
                  school_review: 'in_progress',
                  interview: 'pending',
                  offer_received: 'pending'
                },
                stepDetails: {
                  material_review: { status: 'completed', completedAt: '2024-01-25 11:00:00' },
                  application_submit: { status: 'completed', completedAt: '2024-01-28 16:45:00' },
                  school_review: { status: 'in_progress', completedAt: null },
                  interview: { status: 'pending', completedAt: null },
                  offer_received: { status: 'pending', completedAt: null }
                }
              },
              visa: {
                status: 'pending',
                stepStatuses: {
                  visa_materials: 'pending',
                  visa_application: 'pending',
                  visa_interview: 'pending',
                  visa_result: 'pending'
                },
                stepDetails: {
                  visa_materials: { status: 'pending', completedAt: null },
                  visa_application: { status: 'pending', completedAt: null },
                  visa_interview: { status: 'pending', completedAt: null },
                  visa_result: { status: 'pending', completedAt: null }
                }
              },
              enrollment: {
                status: 'pending',
                stepStatuses: {
                  enrollment_preparation: 'pending',
                  flight_booking: 'pending',
                  accommodation: 'pending',
                  enrollment_registration: 'pending'
                },
                stepDetails: {
                  enrollment_preparation: { status: 'pending', completedAt: null },
                  flight_booking: { status: 'pending', completedAt: null },
                  accommodation: { status: 'pending', completedAt: null },
                  enrollment_registration: { status: 'pending', completedAt: null }
                }
              },
              completed: {
                status: 'pending',
                stepStatuses: {},
                stepDetails: {}
              }
            }
          },
          {
            applicationId: 'APP2024002',
            universityName: '牛津大学',
            programName: '人工智能硕士',
            currentStatus: 'visa',
            statusInfo: {
              text: '签证阶段',
              color: 'warning'
            },
            progress: {
              preparation: {
                status: 'completed',
                stepStatuses: {
                  personal_info: 'completed',
                  application_materials: 'completed',
                  application_fee: 'completed',
                  document_translation: 'completed',
                  document_notarization: 'completed'
                },
                stepDetails: {
                  personal_info: { status: 'completed', completedAt: '2024-01-10 09:15:00' },
                  application_materials: { status: 'completed', completedAt: '2024-01-15 13:30:00' },
                  application_fee: { status: 'completed', completedAt: '2024-01-18 11:45:00' },
                  document_translation: { status: 'completed', completedAt: '2024-01-20 14:30:00' },
                  document_notarization: { status: 'completed', completedAt: '2024-01-22 16:15:00' }
                }
              },
              application: {
                status: 'completed',
                stepStatuses: {
                  material_review: 'completed',
                  application_submit: 'completed',
                  school_review: 'completed',
                  interview: 'completed',
                  offer_received: 'completed'
                },
                stepDetails: {
                  material_review: { status: 'completed', completedAt: '2024-01-20 10:00:00' },
                  application_submit: { status: 'completed', completedAt: '2024-01-25 15:20:00' },
                  school_review: { status: 'completed', completedAt: '2024-02-10 14:30:00' },
                  interview: { status: 'completed', completedAt: '2024-02-15 16:00:00' },
                  offer_received: { status: 'completed', completedAt: '2024-02-25 12:15:00' }
                }
              },
              visa: {
                status: 'in_progress',
                stepStatuses: {
                  visa_materials: 'completed',
                  visa_application: 'in_progress',
                  visa_interview: 'pending',
                  visa_result: 'pending'
                },
                stepDetails: {
                  visa_materials: { status: 'completed', completedAt: '2024-03-01 09:30:00' },
                  visa_application: { status: 'in_progress', completedAt: null },
                  visa_interview: { status: 'pending', completedAt: null },
                  visa_result: { status: 'pending', completedAt: null }
                }
              },
              enrollment: {
                status: 'pending',
                stepStatuses: {
                  enrollment_preparation: 'pending',
                  flight_booking: 'pending',
                  accommodation: 'pending',
                  enrollment_registration: 'pending'
                },
                stepDetails: {
                  enrollment_preparation: { status: 'pending', completedAt: null },
                  flight_booking: { status: 'pending', completedAt: null },
                  accommodation: { status: 'pending', completedAt: null },
                  enrollment_registration: { status: 'pending', completedAt: null }
                }
              },
              completed: {
                status: 'pending',
                stepStatuses: {},
                stepDetails: {}
              }
            }
          },
          {
            applicationId: 'APP2024003',
            universityName: '帝国理工学院',
            programName: '数据科学硕士',
            currentStatus: 'preparation',
            statusInfo: {
              text: '准备阶段',
              color: 'warning'
            },
            progress: {
              preparation: {
                status: 'in_progress',
                stepStatuses: {
                  personal_info: 'completed',
                  application_materials: 'in_progress',
                  application_fee: 'pending',
                  document_translation: 'pending',
                  document_notarization: 'pending'
                },
                stepDetails: {
                  personal_info: { status: 'completed', completedAt: '2024-03-01 10:00:00' },
                  application_materials: { status: 'in_progress', completedAt: null },
                  application_fee: { status: 'pending', completedAt: null },
                  document_translation: { status: 'pending', completedAt: null },
                  document_notarization: { status: 'pending', completedAt: null }
                }
              },
              application: {
                status: 'pending',
                stepStatuses: {
                  material_review: 'pending',
                  application_submit: 'pending',
                  school_review: 'pending',
                  interview: 'pending',
                  offer_received: 'pending'
                },
                stepDetails: {
                  material_review: { status: 'pending', completedAt: null },
                  application_submit: { status: 'pending', completedAt: null },
                  school_review: { status: 'pending', completedAt: null },
                  interview: { status: 'pending', completedAt: null },
                  offer_received: { status: 'pending', completedAt: null }
                }
              },
              visa: {
                status: 'pending',
                stepStatuses: {
                  visa_materials: 'pending',
                  visa_application: 'pending',
                  visa_interview: 'pending',
                  visa_result: 'pending'
                },
                stepDetails: {
                  visa_materials: { status: 'pending', completedAt: null },
                  visa_application: { status: 'pending', completedAt: null },
                  visa_interview: { status: 'pending', completedAt: null },
                  visa_result: { status: 'pending', completedAt: null }
                }
              },
              enrollment: {
                status: 'pending',
                stepStatuses: {
                  enrollment_preparation: 'pending',
                  flight_booking: 'pending',
                  accommodation: 'pending',
                  enrollment_registration: 'pending'
                },
                stepDetails: {
                  enrollment_preparation: { status: 'pending', completedAt: null },
                  flight_booking: { status: 'pending', completedAt: null },
                  accommodation: { status: 'pending', completedAt: null },
                  enrollment_registration: { status: 'pending', completedAt: null }
                }
              },
              completed: {
                status: 'pending',
                stepStatuses: {},
                stepDetails: {}
              }
            }
          }
        ],
        // 个人信息详细数据
        personalInfo: {
          name: '张三',
          name_cn: '张三',
          travel_doc_no: formData.chineseName,
          id_card: '110101199001011234',
          email: 'zhangsan@example.com',
          phone: '13800138000',
          gender: '男',
          date_of_birth: '1990-01-01',
          nationality: '中国',
          religion: '无',
          marital_status: '未婚',
          blood_type: 'A',
          date_of_issue: '2020-01-01',
          date_of_expiry: '2030-01-01',
          place_of_issue: '北京市公安局',
          name_of_mother: '李四',
          name_of_father: '张五',
          siblings_count: '1',
          siblings_ranking: '1',
          monthly_salary: '8000',
          body_height: '175',
          body_weight: '70',
          if_illness: '无',
          apply_degree: '本科',
          intake: '2024年9月',
          english_language_type: 'IELTS',
          english_language_proficiency: '6.5',
          category: '自费',
          agent: '某某留学机构',
          remark: '优秀学生'
        }
      };
      }

      setApplicationData(applicationData);
      setShowBindButton(true); // 查询到结果后显示绑定按钮

    } catch (err) {
      console.error('查询申请进度错误:', err);
      
      // 根据错误类型显示不同的错误信息
      let errorMessage = '查询失败，请重试';
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 404) {
        errorMessage = '未找到相关申请信息，请检查输入信息';
      } else if (err.response?.status === 401) {
        errorMessage = '验证码错误，请重新验证';
      } else if (err.response?.status >= 500) {
        errorMessage = '服务器错误，请稍后重试';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        width: '100%', 
        py: 4, 
        minHeight: '100vh',
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.1)} 0%, 
          ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          animation: 'float 6s ease-in-out infinite',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.1)})`,
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* 页面标题 */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            申请进度查询
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isLoggedIn ? '系统将自动显示您已绑定的申请信息，或输入姓名和编号查询新的申请' : '请先登录后查询申请进度'}
          </Typography>
        </Box>

        {(isPageLoading || userContextLoading) ? (
          /* 页面加载状态 */
          <Card sx={{ maxWidth: 600, mx: 'auto' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                正在加载...
              </Typography>
            </CardContent>
          </Card>
        ) : applicationData ? (
          /* 有申请数据 - 显示申请进度 */
          /* 进度展示 */
          <Box>
            {/* 个人信息概览 */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    个人信息
                  </Typography>
                  <IconButton
                    onClick={() => setPersonalInfoExpanded(!personalInfoExpanded)}
                    sx={{ ml: 1 }}
                  >
                    {personalInfoExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                
                {/* 基本信息 - 永久展示 */}
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">姓名</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.name || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">中文姓名</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.nameCn || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">邮箱</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.email || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">手机号</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.phone || '-'}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Collapse in={personalInfoExpanded}>
                  {/* 个人详情 */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                      个人详情
                    </Typography>
                    <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">性别</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.gender || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">出生日期</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.dateOfBirth || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">国籍</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.nationality || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">信仰</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.religion || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">婚姻状况</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.maritalStatus || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">血型</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.bloodType || '-'}</Typography>
                    </Grid>
                    </Grid>
                  </Box>

                <Divider sx={{ my: 2 }} />

                  {/* 证件信息 */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                      证件信息
                    </Typography>
                    <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">护照号</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.passportNumber || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">身份证号</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.idCard || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">签发日期</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.dateOfIssue || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">到期日期</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.dateOfExpiry || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">签发地</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.placeOfIssue || '-'}</Typography>
                    </Grid>
                    </Grid>
                  </Box>

                <Divider sx={{ my: 2 }} />

                  {/* 家庭信息 */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                      家庭信息
                    </Typography>
                    <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">母亲姓名</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.nameOfMother || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">父亲姓名</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.nameOfFather || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">兄弟姐妹数量</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.siblingsCount || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">排行</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.siblingsRanking || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">月薪(元)</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.monthlySalary || '-'}</Typography>
                    </Grid>
                    </Grid>
                  </Box>

                <Divider sx={{ my: 2 }} />

                  {/* 身体信息 */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                      身体信息
                    </Typography>
                    <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">身高(cm)</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.bodyHeight || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">体重(kg)</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.bodyWeight || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">是否重疾</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.ifIllness || '-'}</Typography>
                    </Grid>
                    </Grid>
                  </Box>

                <Divider sx={{ my: 2 }} />

                  {/* 语言信息 */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                      语言信息
                    </Typography>
                    <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography variant="body2" color="text.secondary">英语语言类型</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.englishLanguageType || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography variant="body2" color="text.secondary">英语语言成绩</Typography>
                      <Typography variant="body1" fontWeight={500}>{applicationData.personalInfo?.englishLanguageProficiency || '-'}</Typography>
                    </Grid>
                    </Grid>
                  </Box>

                </Collapse>
              </CardContent>
            </Card>

            {/* 申请进度标签页 */}
            {applicationData.applications && applicationData.applications.length > 1 && (
              <Card sx={{ mb: 4 }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', overflow: 'auto', p: 1 }}>
                      {applicationData.applications.map((application, index) => {
                        // 根据statusInfo确定颜色
                        const getStatusColor = (statusInfo) => {
                          if (!statusInfo) return theme.palette.grey[500];
                          switch (statusInfo.color) {
                            case 'success':
                              return theme.palette.success.main;
                            case 'error':
                              return theme.palette.error.main;
                            case 'warning':
                              return theme.palette.warning.main;
                            default:
                              return theme.palette.grey[500];
                          }
                        };

                        const statusColor = getStatusColor(application.statusInfo);
                        const isActive = activeApplicationIndex === index;

                        return (
                        <Button
                          key={application.applicationId}
                          onClick={() => setActiveApplicationIndex(index)}
                          sx={{
                            minWidth: 'auto',
                            px: 2,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? statusColor : 'text.secondary',
                            borderRadius: 2,
                            mx: 0.5,
                            backgroundColor: isActive 
                              ? alpha(statusColor, 0.1) 
                              : 'transparent',
                            border: isActive 
                              ? `2px solid ${statusColor}` 
                              : `1px solid ${alpha(statusColor, 0.3)}`,
                            '&:hover': {
                              backgroundColor: isActive 
                                ? alpha(statusColor, 0.15)
                                : alpha(statusColor, 0.04),
                            }
                          }}
                        >
                          <Box sx={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.5,
                                backgroundColor: isActive 
                                  ? statusColor 
                                  : alpha(statusColor, 0.2),
                                color: isActive 
                                  ? 'white' 
                                  : statusColor,
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                minWidth: 'fit-content'
                              }}
                            >
                              {application.universityName}
                            </Box>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ 
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '120px'
                              }}
                            >
                              {application.programName}
                            </Typography>
                          </Box>
                        </Button>
                        );
                      })}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* 指南文章 */}
            {(() => {
              const currentApp = applicationData.applications ? applicationData.applications[activeApplicationIndex] : applicationData;
              console.log('当前申请数据:', currentApp);
              console.log('指南数据:', currentApp?.guides);
              return currentApp && currentApp.guides && currentApp.guides.length > 0;
            })() && (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ArticleIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight={600}>
                      留学指南
                    </Typography>
                  </Box>
                  <Stack spacing={1}>
                    {(() => {
                      const currentApp = applicationData.applications ? applicationData.applications[activeApplicationIndex] : applicationData;
                      return currentApp.guides.map((guide, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        startIcon={<ArticleIcon />}
                        onClick={() => showGuideDialog(guide.title, guide.content, guide.attachments || [])}
                        sx={{
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          textTransform: 'none',
                          p: 2,
                          borderColor: 'primary.light',
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {guide.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            点击查看详细内容
                            {guide.attachments && guide.attachments.length > 0 && (
                              <span> • {guide.attachments.length} 个附件</span>
                            )}
                          </Typography>
                        </Box>
                      </Button>
                      ));
                    })()}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* 进度展示 */}
            <ProgressTracker 
              applicationData={applicationData.applications ? applicationData.applications[activeApplicationIndex] : applicationData}
              activePhase={applicationData.applications ? applicationData.applications[activeApplicationIndex].currentStatus : applicationData.currentStatus}
              showDetails={true}
            />

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setApplicationData(null);
                    setFormData({ chineseName: '', userNumber: '' });
                    setActiveApplicationIndex(0);
                    setShowBindButton(false);
                    setIsBound(false);
                    setIsUserStudentLoaded(false);
                    setIsPageLoading(true);
                  }}
                >
                  重新查询
                </Button>
                
                {showBindButton && (
                  <Button
                    variant="contained"
                    startIcon={isBinding ? <CircularProgress size={20} /> : <LinkIcon />}
                    disabled={isBinding}
                    onClick={showPassportVerificationDialog}
                    sx={{ 
                      backgroundColor: 'success.main',
                      '&:hover': {
                        backgroundColor: 'success.dark',
                      }
                    }}
                  >
                    {isBinding ? '绑定中...' : '绑定到我的账户'}
                  </Button>
                )}
                
                {isBound && (
                  <Button
                    variant="outlined"
                    startIcon={isUnbinding ? <CircularProgress size={20} /> : <LinkOffIcon />}
                    disabled={isUnbinding}
                    onClick={() => showConfirmDialog(
                      '确认解绑',
                      '确定要解绑此申请信息吗？解绑后您需要重新输入姓名和编号才能查询申请进度。',
                      handleUnbindStudent
                    )}
                    sx={{ 
                      borderColor: 'error.main',
                      color: 'error.main',
                      '&:hover': {
                        borderColor: 'error.dark',
                        backgroundColor: 'error.light',
                        color: 'error.dark',
                      }
                    }}
                  >
                    {isUnbinding ? '解绑中...' : '解绑申请信息'}
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>
        ) : (
          /* 没有申请数据 - 显示查询表单 */
          <Card sx={{ maxWidth: 600, mx: 'auto' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              {!isLoggedIn ? (
                /* 未登录状态 - 显示登录提示 */
                <Stack spacing={3}>
                  <Box sx={{ mb: 3 }}>
                    <SecurityIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      需要登录才能查询
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      请先登录您的账户，然后才能查询申请进度
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => {
                      setLoginDialogOpen(true);
                    }}
                    sx={{ py: 1.5 }}
                  >
                    立即登录
                  </Button>
                </Stack>
              ) : (
                /* 已登录状态 - 显示查询表单 */
                <form onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="中文姓名"
                      value={formData.chineseName}
                      onChange={handleInputChange('chineseName')}
                      required
                      placeholder="请输入中文姓名"
                      variant="outlined"
                    />

                    <TextField
                      fullWidth
                      label="编号"
                      value={formData.userNumber}
                      onChange={handleInputChange('userNumber')}
                      required
                      placeholder="请输入编号"
                      variant="outlined"
                    />

                    {error && (
                      <Alert severity="error">
                        {error}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                      disabled={loading}
                      sx={{ py: 1.5 }}
                    >
                      {loading ? '查询中...' : '查询进度'}
                    </Button>
                  </Stack>
                </form>
              )}
            </CardContent>
          </Card>
        )}
      </Container>

      {/* 登录对话框 */}
      <MultiLoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        onLoginSuccess={() => {
          // 登录成功后关闭对话框，UserContext会自动更新登录状态
          setLoginDialogOpen(false);
        }}
      />

      {/* 确认对话框 */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {confirmDialog.content}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} color="primary">
            取消
          </Button>
          <Button 
            onClick={handleConfirmAction} 
            color="primary" 
            autoFocus
            variant="contained"
          >
            确认
          </Button>
        </DialogActions>
      </Dialog>

      {/* 护照号验证对话框 */}
      <Dialog
        open={passportVerificationDialog.open}
        onClose={closePassportVerificationDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="passport-verification-dialog-title"
      >
        <DialogTitle id="passport-verification-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
            身份验证
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            为了确保账户安全，请输入完整的护照号进行身份验证：
          </Typography>
          <TextField
            fullWidth
            label="护照号"
            value={passportVerificationDialog.passportNumber}
            onChange={handlePassportNumberChange}
            placeholder="请输入完整护照号"
            variant="outlined"
            error={!!passportVerificationDialog.error}
            helperText={passportVerificationDialog.error}
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary">
            提示：请输入与申请信息中一致的完整护照号
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={closePassportVerificationDialog}
            variant="outlined"
          >
            取消
          </Button>
          <Button 
            onClick={handleBindStudent}
            variant="contained"
            disabled={isBinding}
            startIcon={isBinding ? <CircularProgress size={20} /> : null}
          >
            {isBinding ? '验证中...' : '确认绑定'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 指南文章对话框 */}
      <Dialog
        open={guideDialog.open}
        onClose={closeGuideDialog}
        maxWidth="md"
        fullWidth
        aria-labelledby="guide-dialog-title"
      >
        <DialogTitle 
          id="guide-dialog-title"
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            pb: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ArticleIcon sx={{ mr: 1, color: 'primary.main' }} />
            {guideDialog.title}
          </Box>
          <IconButton
            onClick={closeGuideDialog}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* 附件列表 */}
          {guideDialog.attachments && guideDialog.attachments.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <AttachFileIcon sx={{ mr: 1, color: 'primary.main' }} />
                附件 ({guideDialog.attachments.length})
              </Typography>
              <List sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
                {guideDialog.attachments.map((attachment, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: 'white',
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <ListItemIcon>
                      {getFileIcon(attachment.original_name || attachment.name)}
                    </ListItemIcon>
                    <ListItemText
                      primary={attachment.original_name || attachment.name || '未知文件'}
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(attachment.size)}
                          </Typography>
                          {attachment.mime_type && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              • {attachment.mime_type}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={async () => {
                          try {
                            let downloadUrl = attachment.url || attachment.path;
                            
                            // 如果URL是相对路径，则拼接API基础URL + storage + 文件路径
                            if (downloadUrl && !downloadUrl.startsWith('http')) {
                              // 移除API基础URL中的/api后缀，然后添加storage路径
                              const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
                              // 确保文件路径以/开头
                              const filePath = downloadUrl.startsWith('/') ? downloadUrl : `/${downloadUrl}`;
                              downloadUrl = `${baseUrl}/storage${filePath}`;
                            }
                            
                            // 获取JWT token用于认证
                            const token = localStorage.getItem('auth_token');
                            
                            // 使用fetch下载文件，避免浏览器直接打开
                            const response = await fetch(downloadUrl, {
                              method: 'GET',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                              },
                            });
                            
                            if (!response.ok) {
                              throw new Error('下载失败');
                            }
                            
                            // 获取文件blob
                            const blob = await response.blob();
                            
                            // 创建下载链接
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = attachment.original_name || attachment.name || 'download';
                            
                            // 强制下载，不在浏览器中打开
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            
                            // 清理URL对象
                            window.URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error('下载文件失败:', error);
                            // 如果fetch失败，回退到直接链接下载
                            const link = document.createElement('a');
                            let downloadUrl = attachment.url || attachment.path;
                            
                            if (downloadUrl && !downloadUrl.startsWith('http')) {
                              const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
                              const filePath = downloadUrl.startsWith('/') ? downloadUrl : `/${downloadUrl}`;
                              downloadUrl = `${baseUrl}/storage${filePath}`;
                            }
                            
                            link.href = downloadUrl;
                            link.download = attachment.original_name || attachment.name || 'download';
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        }}
                        sx={{ color: 'primary.main' }}
                        title="下载文件"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* 指南内容 */}
          <Box
            sx={{
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                color: 'text.primary',
                fontWeight: 600,
                mb: 1,
                mt: 2
              },
              '& h1': { fontSize: '1.5rem' },
              '& h2': { fontSize: '1.3rem' },
              '& h3': { fontSize: '1.1rem' },
              '& p': {
                mb: 1.5,
                lineHeight: 1.6,
                color: 'text.primary'
              },
              '& ul, & ol': {
                pl: 2,
                mb: 1.5
              },
              '& li': {
                mb: 0.5,
                lineHeight: 1.6
              },
              '& strong': {
                fontWeight: 600,
                color: 'text.primary'
              },
              '& em': {
                fontStyle: 'italic',
                color: 'text.secondary'
              },
              '& blockquote': {
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                pl: 2,
                ml: 0,
                fontStyle: 'italic',
                color: 'text.secondary',
                mb: 1.5
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                mb: 1.5
              },
              '& th, & td': {
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
                textAlign: 'left'
              },
              '& th': {
                backgroundColor: 'grey.100',
                fontWeight: 600
              }
            }}
            dangerouslySetInnerHTML={{ __html: guideDialog.content }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={closeGuideDialog} 
            variant="contained"
            fullWidth
          >
            关闭
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApplicationQuery;
